import React from 'react'
import styled from 'styled-components/macro'
import { useSelector } from 'react-redux'
import {
  GROWTH_ENGINE_HUMAN_PATH,
  getGrowthEngineDemoBookingUrl,
  getGrowthEngineWhatsAppUrl,
} from 'src/utils/growthEngineHumanPath'

function useOptionalSelector(selector) {
  try {
    return useSelector(selector)
  } catch {
    return undefined
  }
}

/**
 * Easy human path: demo, WhatsApp, landline, email.
 * On Guided Success cards / empties and paywalls. No upgrade wording.
 */
const GrowthEngineHumanDemoRow = ({
  align = 'start',
  compact = false,
  venueName: venueNameProp,
  featureId,
  featureLabel,
  plan: planProp,
  className,
  'data-testid': dataTestId = 'growth-engine-human-demo-row',
}) => {
  const venueFromStore = useOptionalSelector(state => state.venue?.venue?.title)
  const planFromStore = useOptionalSelector(
    state => state.venue?.venue?.usageLimits?.currentPlan,
  )
  const demoHref = getGrowthEngineDemoBookingUrl()
  const whatsappHref = getGrowthEngineWhatsAppUrl({
    venueName: venueNameProp ?? venueFromStore,
    featureId,
    featureLabel,
    plan: planProp ?? planFromStore,
  })

  return (
    <Wrap
      className={className}
      data-testid={dataTestId}
      data-align={align}
      data-compact={compact ? 'true' : undefined}
      $compact={compact}
      role='group'
      aria-label={GROWTH_ENGINE_HUMAN_PATH.headline}
    >
      <Copy $compact={compact}>
        <Headline $compact={compact}>
          {GROWTH_ENGINE_HUMAN_PATH.headline}
        </Headline>
        {compact ? null : (
          <Subline data-testid='human-demo-subline'>
            {GROWTH_ENGINE_HUMAN_PATH.subline}
          </Subline>
        )}
      </Copy>
      <Links $align={align} $compact={compact}>
        <Link
          $compact={compact}
          href={demoHref}
          target='_blank'
          rel='noopener noreferrer'
          data-testid='human-demo-book'
        >
          {GROWTH_ENGINE_HUMAN_PATH.demoLabel}
        </Link>
        <Link
          $compact={compact}
          href={whatsappHref}
          target='_blank'
          rel='noopener noreferrer'
          data-testid='human-demo-whatsapp'
        >
          {GROWTH_ENGINE_HUMAN_PATH.whatsappLabel}
        </Link>
        <Link
          $compact={compact}
          href={GROWTH_ENGINE_HUMAN_PATH.phoneHref}
          data-testid='human-demo-phone'
        >
          {GROWTH_ENGINE_HUMAN_PATH.phoneLabel}
        </Link>
        <Link
          $compact={compact}
          href={GROWTH_ENGINE_HUMAN_PATH.emailHref}
          data-testid='human-demo-email'
        >
          {GROWTH_ENGINE_HUMAN_PATH.emailLabel}
        </Link>
      </Links>
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: ${({ $compact }) => ($compact ? 'row' : 'column')};
  flex-wrap: ${({ $compact }) => ($compact ? 'wrap' : 'nowrap')};
  align-items: ${({ $compact }) => ($compact ? 'center' : 'stretch')};
  gap: ${({ $compact }) => ($compact ? '4px 12px' : '8px')};
  margin-top: ${({ $compact }) => ($compact ? '0' : '12px')};
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '0' : '4px')};
  min-width: ${({ $compact }) => ($compact ? '0' : '12rem')};
  flex: ${({ $compact }) => ($compact ? '1 1 auto' : '0 1 auto')};
`

const Headline = styled.p`
  color: ${({ theme }) => theme.colours?.oliveGrey || '#555'};
  font-size: ${({ $compact }) => ($compact ? '0.85rem' : '0.95rem')};
  margin: 0;
`

const Subline = styled.p`
  color: ${({ theme }) => theme.colours?.oliveGrey || '#555'};
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.35;
`

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $compact }) => ($compact ? '4px 12px' : '8px 16px')};
  justify-content: ${({ $align }) =>
    $align === 'center' ? 'center' : 'flex-start'};
`

const Link = styled.a`
  background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  border-radius: 999px;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  cursor: pointer;
  display: inline-block;
  font-size: ${({ $compact }) => ($compact ? '0.8rem' : '0.9rem')};
  font-weight: 600;
  padding: ${({ $compact }) => ($compact ? '3px 10px' : '5px 14px')};
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
    opacity: 0.9;
    text-decoration: none;
  }
`

export default GrowthEngineHumanDemoRow
